// @ts-check

import BaseModel from "@kaspernj/api-maker/build/base-model.js"
import CheckIn from "./check-in.js"
import ClassStep from "./class-step.js"

const modelClassData = {
  "attributes": {
    "check_in_id": {
      "column": {
        "default": null,
        "name": "check_in_id",
        "null": false,
        "type": null
      },
      "name": "check_in_id",
      "selected_by_default": null,
      "translated": null
    },
    "class_step_id": {
      "column": {
        "default": null,
        "name": "class_step_id",
        "null": false,
        "type": null
      },
      "name": "class_step_id",
      "selected_by_default": null,
      "translated": null
    },
    "created_at": {
      "column": {
        "default": null,
        "name": "created_at",
        "null": false,
        "type": "datetime"
      },
      "name": "created_at",
      "selected_by_default": null,
      "translated": null
    },
    "id": {
      "column": {
        "default": null,
        "name": "id",
        "null": false,
        "type": null
      },
      "name": "id",
      "selected_by_default": null,
      "translated": null
    },
    "updated_at": {
      "column": {
        "default": null,
        "name": "updated_at",
        "null": false,
        "type": "datetime"
      },
      "name": "updated_at",
      "selected_by_default": null,
      "translated": null
    }
  },
  "className": "CheckInClassStep",
  "collectionKey": "check_in_class_steps",
  "collectionName": "check_in_class_steps",
  "i18nKey": "check_in_class_step",
  "camelizedLower": "checkInClassStep",
  "name": "CheckInClassStep",
  "nameDasherized": "check-in-class-step",
  "pluralName": "check_in_class_steps",
  "ransackable_associations": [
    {
      "className": "CheckInClassStep::Audit",
      "collectionName": "check_in_class_step_audits",
      "foreignKey": "check_in_class_step_id",
      "name": "audits",
      "macro": "has_many",
      "resource_name": "CheckInClassStepAudit",
      "through": null
    },
    {
      "className": "CheckIn",
      "collectionName": "check_ins",
      "foreignKey": "check_in_id",
      "name": "check_in",
      "macro": "belongs_to",
      "resource_name": "CheckIn",
      "through": null
    },
    {
      "className": "ClassStep",
      "collectionName": "class_steps",
      "foreignKey": "class_step_id",
      "name": "class_step",
      "macro": "belongs_to",
      "resource_name": "ClassStep",
      "through": null
    },
    {
      "className": "CheckInClassStep::Audit",
      "collectionName": "check_in_class_step_audits",
      "foreignKey": "check_in_class_step_id",
      "name": "create_audit",
      "macro": "has_one",
      "resource_name": "CheckInClassStepAudit",
      "through": null
    }
  ],
  "ransackable_attributes": [
    {
      "name": "check_in_id",
      "column": {
        "default": null,
        "name": "check_in_id",
        "null": false,
        "type": null
      }
    },
    {
      "name": "class_step_id",
      "column": {
        "default": null,
        "name": "class_step_id",
        "null": false,
        "type": null
      }
    },
    {
      "name": "created_at",
      "column": {
        "default": null,
        "name": "created_at",
        "null": false,
        "type": "datetime"
      }
    },
    {
      "name": "id",
      "column": {
        "default": null,
        "name": "id",
        "null": false,
        "type": null
      }
    },
    {
      "name": "updated_at",
      "column": {
        "default": null,
        "name": "updated_at",
        "null": false,
        "type": "datetime"
      }
    }
  ],
  "ransackable_scopes": [],
  "relationships": [
    {
      "className": "CheckIn",
      "collectionName": "check_ins",
      "foreignKey": "check_in_id",
      "name": "check_in",
      "macro": "belongs_to",
      "resource_name": "CheckIn",
      "through": null
    },
    {
      "className": "ClassStep",
      "collectionName": "class_steps",
      "foreignKey": "class_step_id",
      "name": "class_step",
      "macro": "belongs_to",
      "resource_name": "ClassStep",
      "through": null
    }
  ],
  "paramKey": "check_in_class_step",
  "primaryKey": "id"
}

/** Frontend model for CheckInClassStep. */
export default class CheckInClassStep extends BaseModel {
  /** @returns {typeof modelClassData} */
  static modelClassData() {
    return modelClassData
  }

  /** @returns {any} */
  checkInId() {
    return this.readAttributeUnderscore("check_in_id")
  }

  /** @returns {boolean} */
  hasCheckInId() {
    const value = this.checkInId()

    return this._isPresent(value)
  }

  /** @returns {any} */
  classStepId() {
    return this.readAttributeUnderscore("class_step_id")
  }

  /** @returns {boolean} */
  hasClassStepId() {
    const value = this.classStepId()

    return this._isPresent(value)
  }

  /** @returns {Date} */
  createdAt() {
    return this.readAttributeUnderscore("created_at")
  }

  /** @returns {boolean} */
  hasCreatedAt() {
    const value = this.createdAt()

    return this._isPresent(value)
  }

  /** @returns {any} */
  id() {
    return this.readAttributeUnderscore("id")
  }

  /** @returns {boolean} */
  hasId() {
    const value = this.id()

    return this._isPresent(value)
  }

  /** @returns {Date} */
  updatedAt() {
    return this.readAttributeUnderscore("updated_at")
  }

  /** @returns {boolean} */
  hasUpdatedAt() {
    const value = this.updatedAt()

    return this._isPresent(value)
  }

  /** @returns {import("./check-in.js").default | null} */
  checkIn() {
    return this._readBelongsToReflection({modelClass: CheckIn, reflectionName: "check_in"})
  }

  /** @returns {Promise<import("./check-in.js").default | null>} */
  loadCheckIn() {
    if (!("checkInId" in this)) throw new Error("Foreign key method wasn't defined: checkInId")

    const id = this.checkInId()
    const ransack = {}

    ransack["id_eq"] = id

    return this._loadBelongsToReflection(
      {
        reflectionName: "check_in",
        model: this,
        modelClass: CheckIn
      },
      {ransack}
    )
  }

  /** @returns {import("./class-step.js").default | null} */
  classStep() {
    return this._readBelongsToReflection({modelClass: ClassStep, reflectionName: "class_step"})
  }

  /** @returns {Promise<import("./class-step.js").default | null>} */
  loadClassStep() {
    if (!("classStepId" in this)) throw new Error("Foreign key method wasn't defined: classStepId")

    const id = this.classStepId()
    const ransack = {}

    ransack["id_eq"] = id

    return this._loadBelongsToReflection(
      {
        reflectionName: "class_step",
        model: this,
        modelClass: ClassStep
      },
      {ransack}
    )
  }
}
